import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AddSquare,
  Crown,
  Profile2User,
  RecordCircle,
  TickCircle,
  Trash,
} from "iconsax-react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "@/components/Atoms";
import { CopyLinkRow, CreateShopStepHeading, FloatingTextField } from "@/components/Molecules";
import type { CreateShopTeamStepProps } from "./CreateShopTeamStep.types";
import * as styles from "./CreateShopTeamStep.styles";

export type { CreateShopTeamStepProps } from "./CreateShopTeamStep.types";

export function CreateShopTeamStep({
  inviteRoleCards,
  invitePhone,
  onInvitePhoneChange,
  canAddInvite,
  onAddInvite,
  inviteLink,
  invitedMembers,
  hasInvites,
  inviteCountLabel,
}: CreateShopTeamStepProps) {
  const { t } = useTranslation(["shops", "settings"]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const hasSelectedRole = inviteRoleCards.some((role) => role.selected);

  return (
    <>
      <CreateShopStepHeading
        titleKey="create.team.title"
        subtitleKey="create.team.subtitle"
        mobileSubtitleKey="create.team.mobileSubtitle"
      />

      <Box sx={styles.ownerCard}>
        <Box sx={styles.ownerIcon}>
          <Crown
            size={isDesktop ? 22 : 19}
            variant="Bold"
            color={theme.palette.primary.contrastText}
          />
        </Box>

        <Box sx={styles.ownerContent}>
          <Typography sx={styles.ownerName}>{t("create.team.ownerName")}</Typography>
          <Typography sx={styles.ownerAccess}>
            <Box component="span" sx={styles.mobileOnly}>
              {t("create.team.mobileOwnerAccess")}
            </Box>
            <Box component="span" sx={styles.desktopOnly}>
              {t("create.team.ownerAccess")}
            </Box>
          </Typography>
        </Box>

        <Box component="span" sx={styles.ownerBadge}>
          {t("create.team.ownerBadge")}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>{t("create.team.selectRole")}</Typography>

        <Box sx={styles.roleGrid}>
          {inviteRoleCards.map((role) => {
            const RoleIcon = role.icon;

            return (
              <Box key={role.roleKey} sx={styles.roleCard(role.selected)} onClick={role.onSelect}>
                <RoleIcon
                  size={isDesktop ? 20 : 18}
                  variant="Bold"
                  color={theme.palette.primary.main}
                />

                <Box sx={styles.roleContent}>
                  <Typography sx={styles.roleTitle}>{t(`settings:${role.titleKey}`)}</Typography>
                  <Typography sx={styles.roleDescription}>
                    {t(`settings:${role.descriptionKey}`)}
                  </Typography>
                </Box>

                {role.selected ? (
                  <TickCircle
                    size={isDesktop ? 20 : 18}
                    variant="Bold"
                    color={theme.palette.primary.main}
                  />
                ) : (
                  <RecordCircle
                    size={isDesktop ? 20 : 18}
                    variant="Linear"
                    color={theme.palette.divider}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={styles.sectionDivider}>
        <Typography sx={styles.sectionTitle}>{t("create.team.inviteSection")}</Typography>

        <Box sx={styles.inviteRow}>
          <Box sx={styles.inviteField}>
            <FloatingTextField
              label={t("settings:team.phone")}
              dir="ltr"
              placeholder="09123456789"
              value={invitePhone}
              onChange={(event) => onInvitePhoneChange(event.target.value)}
            />
          </Box>

          <Button
            variant="outlined"
            color="primary"
            disabled={!canAddInvite}
            onClick={onAddInvite}
            sx={styles.outlinedButton}
            startIcon={
              <AddSquare size={18} variant="Linear" color={theme.palette.primary.main} />
            }
          >
            <Box component="span" sx={styles.desktopOnly}>
              {t("create.team.add")}
            </Box>
            <Box component="span" sx={styles.mobileOnly}>
              {t("create.team.addMobile")}
            </Box>
          </Button>
        </Box>

        {!hasSelectedRole ? (
          <Typography sx={styles.roleHint}>{t("create.team.roleHint")}</Typography>
        ) : null}

        <Box sx={styles.dividerRow}>
          <Box sx={styles.dividerLine} />
          <Typography sx={styles.dividerLabel}>{t("create.team.or")}</Typography>
          <Box sx={styles.dividerLine} />
        </Box>

        <Box sx={styles.inviteLinkSection}>
          <Typography sx={styles.inviteLinkTitle}>{t("create.team.inviteLink")}</Typography>
          <CopyLinkRow
            url={inviteLink}
            size={isDesktop ? "lg" : "sm"}
            copyLabel={t("settings:team.copyLink")}
          />
        </Box>

        {hasInvites ? (
          <Box sx={styles.invitedList}>
            <Typography sx={styles.invitedTitle}>
              {t("create.team.invitedTitle", { count: inviteCountLabel })}
            </Typography>

            {invitedMembers.map((member) => {
              const MemberIcon = member.icon;

              return (
                <Box key={member.phone} sx={styles.memberRow}>
                  <Box sx={styles.memberAvatar(member.avatarColor)}>
                    <MemberIcon
                      size={isDesktop ? 17 : 15}
                      variant="Bold"
                      color={theme.palette.common.white}
                    />
                  </Box>

                  <Box sx={styles.memberContent}>
                    <Typography sx={styles.memberPhone} dir="ltr">
                      {member.phone}
                    </Typography>
                    <Box sx={styles.memberMeta}>
                      <StatusBadge
                        label={t(`settings:${member.roleLabelKey}`)}
                        color={member.badgeColor}
                        size="sm"
                      />
                      <Typography sx={styles.memberPending}>{t("create.team.pending")}</Typography>
                    </Box>
                  </Box>

                  <IconButton size="small" onClick={member.onRemove} sx={styles.removeButton}>
                    <Trash
                      size={isDesktop ? 18 : 17}
                      variant="Linear"
                      color={theme.palette.text.secondary}
                    />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={styles.emptyMembers}>
            <Profile2User
              size={isDesktop ? 30 : 26}
              variant="Bold"
              color={theme.palette.primary.main}
            />
            <Typography sx={styles.emptyTitle}>{t("create.team.emptyTitle")}</Typography>
            <Typography sx={styles.emptyDescription}>
              <Box component="span" sx={styles.mobileOnly}>
                {t("create.team.emptyDescriptionMobile")}
              </Box>
              <Box component="span" sx={styles.desktopOnly}>
                {t("create.team.emptyDescription")}
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
